import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { TextField, Button, Alert, Box, Typography } from '@mui/material';
import AuthCard from '../../components/AuthCard';
import { useAuth } from '../../auth/AuthContext';

// Cognito MFA "enter a code" challenge steps. A developer who enrolled TOTP in
// the Vectros Admin app (shared DeveloperUserPool) is challenged here too, so
// the portal must answer rather than reject. (admin-app MR #7.97 §5.)
const MFA_CODE_STEPS = new Set([
  'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  'CONFIRM_SIGN_IN_WITH_EMAIL_CODE',
]);

/**
 * Accept a `returnTo` only if it's an in-app path (single leading slash) —
 * guards against an open-redirect via `?returnTo=//evil.com`, an absolute URL,
 * a `javascript:`/scheme target, or a backslash variant (`/\evil.com` — many
 * browsers normalize `\` to `/`, so `//` and `/\` are equivalent). Returns the
 * safe path or null. (`from` is currently consumed only via react-router
 * `navigate`, which stays in-origin; this guard is defense-in-depth so it
 * stays safe even if a future caller routes it through `window.location`.)
 */
export function sanitizeReturnTo(raw) {
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.includes('\\')) return null;
  return raw;
}

export default function LoginPage() {
  const { login, confirmMfa } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const sessionExpired = params.get('session') === 'expired';
  // `location.state.from` is the full pre-redirect location (RequireAuth.js) — include
  // `.search` too, not just `.pathname`, or a query string like Stripe's post-checkout
  // `?session_id=...` is silently dropped on the way back (#1335).
  const fromLocation = location.state?.from;
  const fromPath = fromLocation ? fromLocation.pathname + (fromLocation.search || '') : null;
  const from = sanitizeReturnTo(params.get('returnTo')) || sanitizeReturnTo(fromPath) || '/dashboard';
  const [error, setError] = useState('');
  const { register, handleSubmit, formState: { isSubmitting } } = useForm();

  // MFA code-entry stage (shown after credentials when Cognito challenges).
  const [mfaStage, setMfaStage] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  const handleStep = (step) => {
    if (step === 'DONE') {
      navigate(from, { replace: true });
      return true;
    }
    if (MFA_CODE_STEPS.has(step)) {
      setMfaStage(true);
      setError('');
      return true;
    }
    // CONTINUE_SIGN_IN_WITH_MFA_SELECTION / TOTP_SETUP — enrollment/selection UI
    // isn't in this portal yet (follow-up MR). Direct the user to the Admin app.
    setError('This account needs two-factor setup. Finish it in the Vectros Admin app, then sign in here.');
    return false;
  };

  const onSubmit = async ({ email, password }) => {
    setError('');
    try {
      const step = await login(email, password);
      handleStep(step);
    } catch (e) {
      setError('Invalid email or password.');
    }
  };

  const onMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMfaSubmitting(true);
    try {
      const step = await confirmMfa(mfaCode.trim());
      handleStep(step);
    } catch (err) {
      setError('That code was incorrect. Please try again.');
    } finally {
      setMfaSubmitting(false);
    }
  };

  if (mfaStage) {
    return (
      <AuthCard title="Verification required" subtitle="Enter the 6-digit code from your authenticator app.">
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={onMfaSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Verification code"
            size="small"
            fullWidth
            autoFocus
            value={mfaCode}
            onChange={(ev) => setMfaCode(ev.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code', maxLength: 6 }}
          />
          <Button type="submit" variant="contained" fullWidth disabled={mfaSubmitting || mfaCode.length !== 6}
            sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
            {mfaSubmitting ? 'Verifying…' : 'Verify'}
          </Button>
          <Button variant="text" onClick={() => { setMfaStage(false); setMfaCode(''); setError(''); }}
            sx={{ textTransform: 'none' }}>
            Back to sign in
          </Button>
        </Box>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Sign in to Vectros" subtitle="Developer Portal">
      {sessionExpired && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>Your session expired. Please sign in again.</Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Email" type="email" size="small" fullWidth
          {...register('email', { required: true })} />
        <TextField label="Password" type="password" size="small" fullWidth
          {...register('password', { required: true })} />
        <Box sx={{ textAlign: 'right', mt: -1 }}>
          <Typography variant="caption">
            <Link to="/forgot-password" style={{ color: '#3B82F6' }}>Forgot password?</Link>
          </Typography>
        </Box>
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting}
          sx={{ bgcolor: '#3B82F6', '&:hover': { bgcolor: '#2563EB' }, textTransform: 'none', fontWeight: 600 }}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </Box>
      <Typography variant="body2" textAlign="center" mt={2} color="text.secondary">
        No account?{' '}
        <Link to="/request-access" style={{ color: '#3B82F6', fontWeight: 500 }}>Request early access</Link>
      </Typography>
    </AuthCard>
  );
}
